import HeroBlock from "@/components/shared/hero-block";
import FancyButton from "@/components/ui/fancy-button";
import { CDN_URL } from "@/consts";
import { cn } from "@/lib/utils";
import { FeatureContent } from "@/lib/features/content";
import { useStrings } from "@/lib/i18n/context";

interface FeatureHeroProps {
  featureContent: FeatureContent;
}

const FeatureHero = ({ featureContent }: FeatureHeroProps) => {
  const s = useStrings();
  const trustedBy = featureContent.trustedBy ?? s.feature.trustedBy;

  return (
    <HeroBlock wrapperStyle="items-center gap-8 md:flex-row md:justify-between">
      <div className="flex flex-col items-start space-y-4 md:w-7/12 md:space-y-7">
        {trustedBy && (
          <div className="flex select-none items-center gap-x-2 rounded-lg bg-[#F5F6FB] px-4 py-3 text-xs font-semibold md:text-sm md:tracking-wider">
            <img
              src={CDN_URL + "/blue-medal.svg"}
              alt="Star"
              className="size-[1.3em]"
              draggable={false}
              style={{ touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}
            />
            {trustedBy}
          </div>
        )}
        <h1 className="text-3xl font-semibold leading-[1.25] text-gray-900 sm:text-4xl sm:leading-[1.25] md:text-5xl md:leading-[1.25] lg:text-6xl lg:leading-[1.25]">
          {featureContent.heroPrefix}{" "}
          {featureContent.heroHighlight && featureContent.heroHighlight !== "" && (
            <>
              <span className="inline rounded-lg bg-[#335CFF] px-[0.1em] py-0 text-white [-webkit-box-decoration-break:clone] [box-decoration-break:clone]">
                {featureContent.heroHighlight}
              </span>{" "}
            </>
          )}
          {featureContent.heroSuffix}
        </h1>

        <p className="answer-first max-w-2xl border-l-2 border-[#E4E7EC] pl-4 text-base leading-relaxed text-gray-600 md:text-lg">
          {featureContent.answer}
        </p>

        <p
          className={cn(
            "text-lg text-gray-700 sm:text-xl md:my-4 md:text-2xl",
            featureContent.heroImage ? "" : "flex items-center gap-1",
          )}
        >
          {featureContent.description}
        </p>

        <FancyButton size="xl" className="ml-1" href={featureContent.buttonLink}>
          {featureContent.buttonText}
        </FancyButton>
      </div>
      {featureContent.heroImage && (
        <div className="relative">
          {featureContent.heroImageFlare && (
            <img
              src={featureContent.heroImageFlare}
              alt={featureContent.title}
              className="absolute -left-6 -top-6 z-50 max-h-[90px] translate-x-[-10%] translate-y-[-10%] -rotate-[25deg] select-none sm:max-h-[100px] lg:max-h-[125px]"
              draggable={false}
            />
          )}
          <img
            src={featureContent.heroImage}
            alt={featureContent.title}
            className="max-h-[300px] w-full max-w-full select-none rounded-2xl border-2 border-[#F7F7F7] bg-[#F7F7F7] object-cover sm:max-h-[350px] md:max-h-[400px] md:w-auto"
            draggable={false}
          />
        </div>
      )}
    </HeroBlock>
  );
};

export default FeatureHero;
